import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule, CardModule } from '@coreui/angular-pro';
import { IconModule } from '@coreui/icons-angular';
import { TranslatePipe } from 'i18n';
import { Logger, Platform } from 'shared';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { CrossLoginService, CrossLoginSite } from '../../services/cross-login.service';

/**
 * Reusable one-click cross-login section. Lists the sibling platforms the
 * logged-in user can switch to (driven by the backend cross_login.json
 * registry) and hands the session over securely on click. Drop
 * `<app-cross-login></app-cross-login>` anywhere a logged-in user is present.
 */
@Component({
  selector: 'app-cross-login',
  standalone: true,
  imports: [CommonModule, ButtonModule, CardModule, IconModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (!loading() && sites().length) {
      <c-card class="cross-login-card">
        <c-card-body>
          <h4 class="mb-2">{{ 'cross_login_title' | translate }}</h4>
          <p class="text-muted mb-3">{{ 'cross_login_description' | translate }}</p>
          <div class="d-flex flex-column flex-sm-row flex-wrap gap-2">
            @for (site of sites(); track site.key) {
              <button
                cButton
                color="primary"
                class="flex-grow-1"
                [disabled]="redirecting() === site.key"
                (click)="go(site)"
              >
                {{ 'cross_login_continue' | translate: { name: site.name } }}
              </button>
            }
          </div>
        </c-card-body>
      </c-card>
    }
  `
})
export class CrossLoginComponent implements OnInit {
  private crossLogin = inject(CrossLoginService);
  private platform = inject(Platform);
  private inAppBrowser = inject(InAppBrowser);
  private logger = new Logger('CrossLoginComponent');

  sites = signal<CrossLoginSite[]>([]);
  loading = signal(true);
  redirecting = signal<string | null>(null);

  ngOnInit(): void {
    this.crossLogin.targets().subscribe({
      next: sites => {
        // Safety net: never offer a button back to the site we're already on
        // (the backend already excludes it; this also covers local dev).
        const host = window.location.hostname.replace(/^www\./, '');
        this.sites.set(
          (sites || []).filter(s => {
            try {
              return new URL(s.url).hostname.replace(/^www\./, '') !== host;
            } catch {
              return true;
            }
          })
        );
        this.loading.set(false);
      },
      error: err => {
        this.logger.error('Failed to load cross-login targets', err);
        this.loading.set(false);
      }
    });
  }

  go(site: CrossLoginSite): void {
    if (this.redirecting()) return;
    this.redirecting.set(site.key);
    this.crossLogin.handoffUrl(site.key).subscribe({
      next: url => this.redirect(url),
      error: err => {
        this.logger.error('Failed to create cross-login handoff', err);
        this.redirecting.set(null);
      }
    });
  }

  private redirect(url: string): void {
    if (this.platform.is('hybrid')) {
      this.inAppBrowser.create(url, '_system');
      this.redirecting.set(null);
    } else {
      window.location.href = url;
    }
  }
}
